/**
 * World geometry constants. These are baked into the wire protocol and the
 * storage format, so changing them invalidates existing saves.
 */

export const CHUNK_X = 16;
export const CHUNK_Z = 16;
export const SECTION_Y = 16;

/** Bottom of the world (inclusive). */
export const WORLD_MIN_Y = -64;
/** Total world height in blocks. */
export const WORLD_HEIGHT = 384;
/** Top of the world (exclusive). */
export const WORLD_MAX_Y = WORLD_MIN_Y + WORLD_HEIGHT;
/** Number of 16-tall sections stacked in one chunk column. */
export const SECTION_COUNT = WORLD_HEIGHT / SECTION_Y;

export const BLOCKS_PER_SECTION = CHUNK_X * SECTION_Y * CHUNK_Z; // 4096
export const NIBBLES_PER_SECTION = BLOCKS_PER_SECTION / 2; // 2048

export const SEA_LEVEL = 63;

/** Server simulation rate. */
export const TPS = 20;
export const MS_PER_TICK = 1000 / TPS;

/** Length of a full day/night cycle, in ticks (20 minutes, as in vanilla). */
export const DAY_LENGTH = 24000;

export const MAX_LIGHT = 15;

/** Index of a block inside a section's flat arrays. */
export function sectionIndex(x: number, y: number, z: number): number {
  return (y << 8) | (z << 4) | x;
}

/** Pack a chunk column coordinate into a single number usable as a Map key. */
export function chunkKey(cx: number, cz: number): number {
  // Chunk coords are clamped to +-2^20 which is far beyond any reachable area.
  return (cx + 0x80000) * 0x100000 + (cz + 0x80000);
}

export function chunkKeyX(key: number): number {
  return Math.floor(key / 0x100000) - 0x80000;
}

export function chunkKeyZ(key: number): number {
  return (key % 0x100000) - 0x80000;
}

/** Pack a block position into a string-free numeric key (safe within +-2^21 on X/Z). */
export function blockKey(x: number, y: number, z: number): number {
  return ((x + 0x400000) * 0x800000 + (z + 0x400000)) * 512 + (y - WORLD_MIN_Y);
}

export function unpackBlockKey(key: number): [number, number, number] {
  const y = (key % 512) + WORLD_MIN_Y;
  const rest = Math.floor(key / 512);
  const z = (rest % 0x800000) - 0x400000;
  const x = Math.floor(rest / 0x800000) - 0x400000;
  return [x, y, z];
}

export const DIMENSIONS = ['overworld', 'nether', 'the_end'] as const;
export type DimensionId = (typeof DIMENSIONS)[number];

/** Coordinate scale when travelling between the overworld and the nether. */
export const NETHER_SCALE = 8;
